/**
 * Utility functions for text selection and markdown detection.
 * Extracted from useTextSelection hook for maintainability.
 */

// Utility to detect if text contains markdown formatting
export function hasMarkdownFormatting(text) {
  if (!text) return false;

  // Common markdown patterns
  const markdownPatterns = [
    /\*\*.*?\*\*/g, // Bold **text**
    /\*.*?\*/g, // Italic *text*
    /`.*?`/g, // Inline code `text`
    /^#{1,6}\s/gm, // Headers # ## ###
    /^[-*]\s/gm, // Bullet lists - item or * item
    /^•\s/gm, // Bullet lists with dot char from rendered summary
    /^\d+\.\s/gm, // Numbered lists 1. item
    /\[.*?\]\(.*?\)/g, // Links [text](url)
    /^>\s/gm, // Blockquotes > text
  ];

  return markdownPatterns.some(pattern => pattern.test(text));
}

// Utility to extract markdown from selected content
export function extractMarkdownFromSelection(selection) {
  const selectedText = selection.toString().trim();
  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;

  // Try to find the closest element that might contain markdown source
  let markdownContainer = container.nodeType === Node.TEXT_NODE
    ? container.parentElement
    : container;

  // Look for data attributes or markdown source (walk up a few ancestors)
  while (markdownContainer && markdownContainer !== document.body) {
    if (markdownContainer.dataset && markdownContainer.dataset.markdown) {
      const fullMarkdown = markdownContainer.dataset.markdown;
      return extractRelevantMarkdown(fullMarkdown, selectedText);
    }
    if (markdownContainer.getAttribute && markdownContainer.getAttribute('data-markdown-source')) {
      const fullMarkdown = markdownContainer.getAttribute('data-markdown-source');
      return extractRelevantMarkdown(fullMarkdown, selectedText);
    }
    // Support elements that mirror markdown in a data-md attribute
    if (markdownContainer.getAttribute && markdownContainer.getAttribute('data-md')) {
      const fullMarkdown = markdownContainer.getAttribute('data-md');
      return extractRelevantMarkdown(fullMarkdown, selectedText);
    }
    markdownContainer = markdownContainer.parentElement;
  }

  // Fallback: if no markdown source found, try to pull markdown from known article containers
  try {
    const el = markdownContainer?.closest('[data-markdown-source], [data-markdown]');
    if (el) {
      const source = el.getAttribute('data-markdown-source') || el.dataset.markdown;
      if (source) {
        const md = extractRelevantMarkdown(source, selectedText);
        if (md && md.trim().length > 0) return md;
      }
    }
  } catch (_) {
    // no-op
  }

  // Last resort: reconstruct minimal markdown from the actual HTML selection
  try {
    const fragment = range.cloneContents();
    const markdown = convertHtmlFragmentToMarkdown(fragment).trim();
    if (markdown && markdown.length > 0) {
      return markdown;
    }
  } catch (_) {
    // ignore
  }
  return selectedText;
}

// Helper function to extract relevant markdown portion based on selected text
export function extractRelevantMarkdown(fullMarkdown, selectedText) {
  if (!fullMarkdown || !selectedText) return selectedText;

  // If the selected text already contains markdown tokens, keep it
  if (hasMarkdownFormatting(selectedText)) {
    return selectedText;
  }

  // Helper: strip most markdown/whitespace for fuzzy matching
  const toPlain = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[#>\-•\d+.]+\s*/gm, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const plainSelected = toPlain(selectedText);
  const lines = fullMarkdown.split('\n');

  // Sliding window over lines to find a minimal block whose plain text contains the selection
  let best = null; // { start, end }
  for (let start = 0; start < lines.length; start++) {
    let acc = '';
    for (let end = start; end < lines.length && end - start < 30; end++) {
      acc += (end > start ? '\n' : '') + lines[end];
      if (toPlain(acc).includes(plainSelected)) {
        if (!best || (end - start) < (best.end - best.start)) {
          best = { start, end };
        }
        break;
      }
    }
  }

  if (best) {
    return lines.slice(best.start, best.end + 1).join('\n');
  }

  // Fallback: return original selected text
  return selectedText;
}

// Convert a DocumentFragment (from selection) back into lightweight markdown
export function convertHtmlFragmentToMarkdown(fragment) {
  const parts = [];

  function walk(node) {
    if (!node) return;
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.nodeValue);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const el = node;
    const tag = el.tagName;

    // Block-level elements -> add newlines around content
    const isBlock = ['P', 'DIV', 'SECTION', 'ARTICLE', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BR'].includes(tag);

    const pushChildren = () => {
      for (let i = 0; i < el.childNodes.length; i++) {
        walk(el.childNodes[i]);
      }
    };

    switch (tag) {
      case 'STRONG':
      case 'B':
        parts.push('**');
        pushChildren();
        parts.push('**');
        break;
      case 'EM':
      case 'I':
        parts.push('*');
        pushChildren();
        parts.push('*');
        break;
      case 'CODE':
        parts.push('`');
        pushChildren();
        parts.push('`');
        break;
      case 'A':
        {
          const href = el.getAttribute('href') || '';
          const textPartsStart = parts.length;
          pushChildren();
          const linkText = parts.splice(textPartsStart).join('');
          parts.push(`[${linkText}](${href})`);
        }
        break;
      case 'LI':
        parts.push('- ');
        pushChildren();
        parts.push('\n');
        break;
      case 'BR':
        parts.push('\n');
        break;
      default:
        if (isBlock) parts.push('\n');
        pushChildren();
        if (isBlock) parts.push('\n');
        break;
    }
  }

  for (let i = 0; i < fragment.childNodes.length; i++) {
    walk(fragment.childNodes[i]);
  }

  // Normalize excess whitespace/newlines
  return parts.join('')
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
